import { useState } from "react";
import { useData } from "../../Context/DataContext";
import { useAuth } from "../../Context/AuthContext";
import ButtonComponent from "../Inputs/ButtonComponent";

export interface IntakeNote {
  date: string;
  notes: string;
  coordinator: string;
}

interface IntakeNotesProps {
  intakeNotes: IntakeNote[]
  intakeId: string | null
  onNoteSubmit?: () => void
}

const NotesForm: React.FC<IntakeNotesProps> = ({
  intakeNotes,
  intakeId,
  onNoteSubmit
}) => {
  const {currentNotes, sendNewNotes, currentTimeZone} = useData();
  const {allUsers} = useAuth()

  const {currentUser} = useAuth();

  const [noteContent, setNoteContent] = useState("");

  const handleSubmitNotes = () => {
    if (!intakeId || !noteContent.trim()) return;

    const data = {
      intake_id: intakeId,
      date: getCurrentDateString(),
      notes: noteContent,
      coordinator: currentUser.userId
    }
    sendNewNotes(data, onNoteSubmit)
    setNoteContent("");
  }

  function getCurrentDateString(): string {
    // Use YYYY-MM-DD format to match the backend expectation
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }

  function reformatDateString(date: string): string {
    if (!date) return "N/A";

    // Parse the date from various formats
    let parsedDate: Date | null = null;

    // Check for MM-DD-YYYY HH:MM AM/PM format
    const withTimeMatch = date.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i);
    if (withTimeMatch) {
      const [, mm, dd, yyyy, hour, minute, period] = withTimeMatch;
      let h = parseInt(hour);
      if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
      if (period.toUpperCase() === 'AM' && h === 12) h = 0;
      parsedDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd), h, parseInt(minute));
    }

    // Check for MM-DD-YYYY format
    if (!parsedDate) {
      const mmddyyyyMatch = date.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (mmddyyyyMatch) {
        const [, mm, dd, yyyy] = mmddyyyyMatch;
        parsedDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd), 12, 0, 0);
      }
    }

    // Check for YYYY-MM-DD format
    if (!parsedDate) {
      const yyyymmddMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (yyyymmddMatch) {
        const [, yyyy, mm, dd] = yyyymmddMatch;
        parsedDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd), 12, 0, 0);
      }
    }

    if (!parsedDate || isNaN(parsedDate.getTime())) {
      return date; // Return as-is if can't parse
    }

    // Get current time in user's timezone
    const now = new Date();
    const nowInTimezone = new Date(now.toLocaleString('en-US', { timeZone: currentTimeZone || 'America/Los_Angeles' }));

    // Calculate difference in days
    const todayStart = new Date(nowInTimezone.getFullYear(), nowInTimezone.getMonth(), nowInTimezone.getDate());
    const dateStart = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());

    const diffTime = todayStart.getTime() - dateStart.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Return relative date string
    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      return `${Math.floor(diffDays / 7)} weeks ago`;
    } else {
      // Past 30 days - show actual date
      return `${parsedDate.getMonth() + 1}/${parsedDate.getDate()}/${parsedDate.getFullYear()}`;
    }
  }


  const getCoordinatorName = (coordinatorId: string) => {
    if (allUsers) {
      const user = allUsers.find((user) => user.userid === coordinatorId);
      return user ? user.name : "N/A"
    }
    return 'N/A'
  };



  return (
    <div className="w-full">
      <table className="min-w-full divide-y divide-primary rounded-lg overflow-hidden">
        <thead className="bg-dark-card border-b border-dark-border min-w-full">
          <tr>
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
              Created
            </th>
            <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
              Notes
            </th>
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
              Name
            </th>
          </tr>
        </thead>
        <tbody className="bg-dark-bg divide-y divide-dark-border">
          {currentNotes && currentNotes.map((note, index) => (
            <tr key={index}>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                {reformatDateString(note.date) || "N/A"}
              </td>
              <td className="px-6 py-4 whitespace-wrap text-sm text-gray-300">
                {note.notes || "N/A"}
              </td>
              <td className="px-6 py-4 whitespace-wrap text-sm text-gray-400">
                {getCoordinatorName(note.coordinator)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <form onSubmit={(e) => { e.preventDefault(); handleSubmitNotes(); }}>
        <div className='flex my-2 px-3 py-2 flex-col justify-center rounded-lg'>
        <p className="font-semibold text-accent-teal text-md mr-4 mb-1">Enter New Note</p>
        <input
          className="flex-1 text-base border border-dark-border bg-dark-input text-white placeholder-gray-500 rounded-xl px-3 py-2 focus:outline-none focus:border-accent-teal"
          value={noteContent}
          onChange={(e) => setNoteContent(e.target.value)}
          placeholder="Add new note..."
        />
        </div>
        <div className="mb-5">
        <ButtonComponent label="Submit Notes" handler={handleSubmitNotes}/>
        </div>
      </form>
    </div>
  )
}

export default NotesForm